/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import swal from "sweetalert";
import { useNavigate, useLocation } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDDropzone from "components/MDDropzone";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { API_ESA_OFFICE, API_ESA_OFFICE_UPLOAD } from "utils";
import { logoutUser } from "actions/AuthAction";

function DisposisiBaru() {
  const navigate = useNavigate();
  const location = useLocation();
  const dropzoneRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [suratList, setSuratList] = useState([]);
  const [selectedSurat, setSelectedSurat] = useState(null);
  const [penerimaOptions, setPenerimaOptions] = useState([]);
  const [selectedPenerima, setSelectedPenerima] = useState([]);
  const [catatan, setCatatan] = useState("");
  const [files, setFiles] = useState([]);
  const [totalSize, setTotalSize] = useState(0);
  const [showSizeWarning, setShowSizeWarning] = useState(false);
  const [tingkatPerintah, setTingkatPerintah] = useState("");
  const [userId, setUserId] = useState(null);
  const [dinasId, setDinasId] = useState(null);
  const [statusUser, setStatusUser] = useState(null);
  const [levelJabatan, setLevelJabatan] = useState(null);
  const tingkatPerintahOptions = [
    { value: "segera", label: "Segera" },
    { value: "biasa", label: "Biasa" },
    { value: "penting", label: "Penting" },
  ];

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setUserId(user.uid);
      setDinasId(user.dinas_id);
      setStatusUser(user.status);
      setLevelJabatan(user.level_jabatan);
    } else {
      logoutUser(navigate);
    }
  }, []);

  useEffect(() => {
    // Fetch daftar surat masuk (hanya bulan dan tahun saat ini)
    if (dinasId) {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11

      axios
        .get(
          `${API_ESA_OFFICE}surat-masuk/dinas/${dinasId}?tahun=${currentYear}&bulan=${currentMonth}&belum_disposisi=true`
        )
        .then((response) => {
          const formattedSurat = response.data.map((surat) => ({
            ...surat,
            label: `${surat.nomor_surat} - ${surat.perihal}`,
            value: surat.surat_id,
            // Tambahkan nama file untuk pencarian
            searchText: `${surat.nomor_surat} ${surat.perihal} ${
              surat.file_surat ? surat.file_surat.split("/").pop() : ""
            }`.toLowerCase(),
          }));
          setSuratList(formattedSurat);

          // Jika ada state dari navigasi, set selectedSurat
          if (location.state?.selectedSurat) {
            const suratFromState = location.state.selectedSurat;
            // Cari surat yang sesuai dari list yang sudah di-fetch
            const foundSurat = formattedSurat.find(
              (s) =>
                s.surat_id === suratFromState.surat_id ||
                s.value === suratFromState.value
            );
            if (foundSurat) {
              setSelectedSurat(foundSurat);
            } else {
              // Jika tidak ditemukan di list (karena filter bulan/tahun),
              // tambahkan ke list dan set sebagai selected
              const suratToAdd = {
                ...suratFromState,
                surat_id: suratFromState.surat_id || suratFromState.value,
                label:
                  suratFromState.label ||
                  `${suratFromState.nomor_surat} - ${suratFromState.perihal}`,
                value: suratFromState.value || suratFromState.surat_id,
              };
              setSuratList((prev) => [suratToAdd, ...prev]);
              setSelectedSurat(suratToAdd);
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching surat:", error);
          // Jika error tapi ada state, tetap set selectedSurat
          if (location.state?.selectedSurat) {
            const suratFromState = location.state.selectedSurat;
            const suratToAdd = {
              ...suratFromState,
              surat_id: suratFromState.surat_id || suratFromState.value,
              label:
                suratFromState.label ||
                `${suratFromState.nomor_surat} - ${suratFromState.perihal}`,
              value: suratFromState.value || suratFromState.surat_id,
            };
            setSelectedSurat(suratToAdd);
          }
        });

      // Fetch daftar penerima potensial

      axios
        .get(
          statusUser === "pimpinan"
            ? `${API_ESA_OFFICE}users/pimpinan/list`
            : `${API_ESA_OFFICE}users/dinas/${dinasId}`
        )
        .then((response) => {
          const options = response.data.map((user) => ({
            label: user.nama,
            value: user.user_id,
            avatar: user.uid_RSS
              ? `https://smartdh.rotendaokab.go.id/uploads/${user.uid_RSS}.jpg`
              : undefined,
          }));
          setPenerimaOptions(options);
        })
        .catch((error) => {
          console.error("Error fetching users:", error);
        });
    }
  }, [dinasId]);

  // Event handler untuk memvalidasi dan menolak file sebelum ditambahkan ke dropzone
  useEffect(() => {
    const dz = dropzoneRef.current?.dropzone;
    if (!dz) return;

    // Handler untuk memvalidasi file sebelum ditambahkan (menggunakan accept)
    const handleAccept = (file, done) => {
      // Hitung total ukuran file yang sudah ada di dropzone
      const existingFilesSize = dz.files.reduce((sum, f) => {
        return sum + (f.size || 0);
      }, 0);

      // Hitung total ukuran setelah menambahkan file baru
      const totalSize = existingFilesSize + file.size;
      const maxTotalSize = 1 * 1024 * 1024; // 1 MB
      const totalSizeMB = totalSize / (1024 * 1024);

      // Jika total ukuran melebihi batas, tolak file
      if (totalSize > maxTotalSize) {
        done(`Total ukuran melebihi 1 MB (${totalSizeMB.toFixed(2)} MB)`);

        // Tampilkan pesan error
        swal({
          title: "Ukuran File Melebihi Batas",
          text: `Total ukuran semua file (${totalSizeMB.toFixed(
            2
          )} MB) melebihi batas maksimal 1 MB.\n\nSilakan hapus beberapa file yang sudah ada atau pilih file yang lebih kecil.`,
          icon: "error",
          button: "OK",
        });
        return;
      }

      // File diterima
      done(null);
    };

    // Handler untuk memvalidasi multiple files (menggunakan addedfiles sebagai fallback)
    const handleAddedFiles = (files) => {
      // Gunakan setTimeout untuk memastikan file sudah ditambahkan ke dz.files
      setTimeout(() => {
        // Hitung total ukuran file yang sekarang ada di dropzone
        const allFiles = dz.files;
        const totalSize = allFiles.reduce((sum, file) => {
          return sum + (file.size || 0);
        }, 0);
        const maxTotalSize = 1 * 1024 * 1024; // 1 MB
        const totalSizeMB = totalSize / (1024 * 1024);

        // Jika total ukuran melebihi batas, hapus file yang baru ditambahkan
        if (totalSize > maxTotalSize) {
          // Hapus file yang baru ditambahkan (file yang ada di parameter files)
          files.forEach((file) => {
            // Hapus file yang menyebabkan total melebihi batas
            if (dz.files.includes(file)) {
              dz.emit("error", file, `Total ukuran melebihi 1 MB`);
              dz.removeFile(file);
            }
          });

          // Tampilkan pesan error
          swal({
            title: "Ukuran File Melebihi Batas",
            text: `Total ukuran semua file (${totalSizeMB.toFixed(
              2
            )} MB) melebihi batas maksimal 1 MB.\n\nSilakan hapus beberapa file yang sudah ada atau pilih file yang lebih kecil.`,
            icon: "error",
            button: "OK",
          });
        }
      }, 0);
    };

    // Tambahkan event listener untuk accept (validasi sebelum file ditambahkan)
    dz.on("accept", handleAccept);

    // Tambahkan event listener untuk addedfiles sebagai fallback
    dz.on("addedfiles", handleAddedFiles);

    // Cleanup: hapus event listener saat unmount
    return () => {
      if (dz) {
        dz.off("accept", handleAccept);
        dz.off("addedfiles", handleAddedFiles);
      }
    };
  }, []);

  const handleDropzoneChange = (files) => {
    const totalSizeInMB =
      files.reduce((acc, file) => acc + file.size, 0) / (1024 * 1024);
    const maxSizeMB = 1; // Batas maksimal 1 MB

    setTotalSize(totalSizeInMB);
    setShowSizeWarning(totalSizeInMB > maxSizeMB);

    // Hanya set files jika tidak melebihi batas
    // File yang melebihi batas sudah ditolak oleh event handler accept/addedfiles
    if (totalSizeInMB <= maxSizeMB) {
      setFiles(files);
    }
  };

  const renderPreview = () => {
    if (files.length === 0) return null;

    return (
      <MDBox mt={2}>
        <Grid container spacing={2}>
          {files.map((file, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card>
                <MDBox p={2}>
                  <MDTypography variant="caption" color="text">
                    {file.name}
                  </MDTypography>
                  <MDTypography variant="caption" color="text" display="block">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
          ))}
        </Grid>
        {showSizeWarning && (
          <MDBox mt={2}>
            <MDTypography variant="caption" color="error" fontWeight="regular">
              ⚠️ Total ukuran file ({totalSize.toFixed(2)} MB) melebihi batas
              maksimal (1 MB). File tidak dapat disimpan.
            </MDTypography>
          </MDBox>
        )}
        <MDBox mt={1}>
          <MDTypography
            variant="caption"
            color={totalSize > 1 ? "error" : "text"}
            fontWeight="regular"
          >
            Total ukuran: {totalSize.toFixed(2)} MB / 1 MB
          </MDTypography>
        </MDBox>
      </MDBox>
    );
  };

  const handleSubmit = async () => {
    if (
      !selectedSurat ||
      selectedPenerima.length === 0 ||
      !catatan ||
      !tingkatPerintah
    ) {
      swal({
        title: "Data Tidak Lengkap",
        text: "Mohon lengkapi semua data yang diperlukan",
        icon: "warning",
        button: "OK",
      });
      return;
    }

    // Validasi userId
    if (!userId || userId === "null") {
      swal({
        title: "Error",
        text: "ID Pengguna tidak valid. Silakan login kembali.",
        icon: "error",
        button: "OK",
      });
      return;
    }

    // Validasi total ukuran file (maksimal 1 MB)
    const totalSizeInMB =
      files.reduce((acc, file) => acc + file.size, 0) / (1024 * 1024);
    if (totalSizeInMB > 1) {
      swal({
        title: "Ukuran File Terlalu Besar!",
        text: `Total ukuran file (${totalSizeInMB.toFixed(
          2
        )} MB) melebihi batas maksimal (1 MB). File tidak dapat disimpan.`,
        icon: "error",
        button: "OK",
      });
      return;
    }

    setLoading(true);
    const formData = new FormData();

    // Pastikan semua data yang dikirim adalah string yang valid
    formData.append("surat_id", selectedSurat.value.toString());
    formData.append("dari_id", userId.toString()); // Pastikan dari_id adalah string numerik
    formData.append(
      "tanggal_disposisi",
      new Date().toISOString().split("T")[0]
    );
    formData.append("catatan_disposisi", catatan);
    formData.append("tingkat_perintah", tingkatPerintah);

    // Pastikan penerima adalah array yang valid
    const penerimaIds = selectedPenerima.map((p) => p.value.toString());
    formData.append("penerima", JSON.stringify(penerimaIds));

    // Append multiple files
    if (files.length > 0) {
      files.forEach((file) => {
        formData.append("file_disposisi", file);
      });
    }

    try {
      const response = await axios.post(
        `${API_ESA_OFFICE}disposisi`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data) {
        const disposisiId = response.data?.disposisi_id;
        swal({
          icon: "success",
          title: "Berhasil!",
          text: "Disposisi berhasil dibuat",
          button: "OK",
        }).then(() => {
          // Reset form setelah berhasil
          setSelectedSurat(null);
          setSelectedPenerima([]);
          setCatatan("");
          setTingkatPerintah("");
          setFiles([]);
          setTotalSize(0);
          setShowSizeWarning(false);
          // Clear dropzone files
          if (dropzoneRef.current && dropzoneRef.current.dropzone) {
            const dz = dropzoneRef.current.dropzone;
            const filesToRemove = [...dz.files];
            filesToRemove.forEach((file) => {
              dz.removeFile(file);
            });
          }
          // Navigasi ke halaman detail disposisi yang baru dibuat
          if (disposisiId) {
            navigate(`/task-management/disposisi/disposisi-detail/${disposisiId}`);
          }
        });
      }
    } catch (error) {
      console.error("Error creating disposisi:", error);
      const errorMessage =
        error.response?.data?.message || "Gagal membuat disposisi";
      const errorDetails = error.response?.data?.details;

      if (errorDetails) {
        // Tampilkan detail error jika ada
        const errorText = Object.entries(errorDetails)
          .filter(([_, value]) => value !== null)
          .map(([key, value]) => `${key}: ${value}`)
          .join("\n");
        swal({
          icon: "error",
          title: "Error",
          text: `${errorMessage}\n\nDetail:\n${errorText}`,
          button: "OK",
        });
      } else {
        swal({
          icon: "error",
          title: "Error",
          text: errorMessage,
          button: "OK",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Buat Disposisi Baru
                </MDTypography>
              </MDBox>
              <MDBox pt={4} pb={3} px={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Autocomplete
                      options={suratList}
                      getOptionLabel={(option) => option.label || ""}
                      value={selectedSurat}
                      onChange={(event, newValue) => setSelectedSurat(newValue)}
                      filterOptions={(options, { inputValue }) => {
                        // Pencarian berdasarkan nomor surat, perihal, dan nama file lampiran
                        const searchText = inputValue.toLowerCase();
                        return options.filter((option) => {
                          const nomorSurat = (
                            option.nomor_surat || ""
                          ).toLowerCase();
                          const perihal = (option.perihal || "").toLowerCase();
                          const fileSurat = option.file_surat
                            ? option.file_surat.split("/").pop().toLowerCase()
                            : "";
                          return (
                            nomorSurat.includes(searchText) ||
                            perihal.includes(searchText) ||
                            fileSurat.includes(searchText)
                          );
                        });
                      }}
                      renderInput={(params) => (
                        <MDInput
                          {...params}
                          label="Pilih Surat (Bulan & Tahun Saat Ini)"
                          variant="standard"
                          helperText="Cari berdasarkan nomor surat, perihal, atau nama file lampiran"
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <div
                            style={{ display: "flex", flexDirection: "column" }}
                          >
                            <span style={{ fontWeight: "bold" }}>
                              {option.nomor_surat}
                            </span>
                            <span
                              style={{
                                fontSize: "0.875rem",
                                color: "rgba(0, 0, 0, 0.6)",
                              }}
                            >
                              {option.perihal}
                            </span>
                            {option.file_surat && (
                              <span
                                style={{
                                  fontSize: "0.75rem",
                                  color: "rgba(0, 0, 0, 0.4)",
                                  marginTop: "4px",
                                }}
                              >
                                📎 {option.file_surat.split("/").pop()}
                              </span>
                            )}
                          </div>
                        </li>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={penerimaOptions}
                      value={selectedPenerima}
                      onChange={(event, newValue) =>
                        setSelectedPenerima(newValue)
                      }
                      renderInput={(params) => (
                        <MDInput
                          {...params}
                          label="Pilih Penerima Disposisi"
                          variant="standard"
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.value}
                            label={option.label}
                            avatar={
                              option.avatar ? (
                                <img
                                  src={option.avatar}
                                  alt={option.label}
                                  style={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: "50%",
                                  }}
                                />
                              ) : null
                            }
                          />
                        ))
                      }
                      renderOption={(props, option) => (
                        <li {...props}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            {option.avatar && (
                              <img
                                src={option.avatar}
                                alt={option.label}
                                style={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: "50%",
                                }}
                              />
                            )}
                            <span>{option.label}</span>
                          </div>
                        </li>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      options={tingkatPerintahOptions}
                      value={
                        tingkatPerintahOptions.find(
                          (option) => option.value === tingkatPerintah
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setTingkatPerintah(newValue?.value || "")
                      }
                      getOptionLabel={(option) => option.label}
                      renderInput={(params) => (
                        <MDInput
                          {...params}
                          label="Tingkat Perintah"
                          variant="standard"
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <span
                              style={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                backgroundColor:
                                  option.value === "segera"
                                    ? "#f44336"
                                    : option.value === "penting"
                                    ? "#ff9800"
                                    : "#4caf50",
                              }}
                            />
                            <span>{option.label}</span>
                          </div>
                        </li>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Catatan Disposisi"
                      multiline
                      rows={4}
                      fullWidth
                      value={catatan}
                      onChange={(e) => setCatatan(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    {renderPreview()}
                    <MDBox mb={2}>
                      <MDTypography
                        variant="caption"
                        color="text"
                        fontWeight="regular"
                      >
                        * Maksimal total ukuran file: 1 MB
                        <br />* Format yang didukung: PDF, DOC, DOCX
                      </MDTypography>
                    </MDBox>
                    {useMemo(
                      () => (
                        <MDDropzone
                          options={{
                            addRemoveLinks: true,
                            uploadMultiple: true,
                            maxFilesize: 1, // dalam MB
                            maxFiles: 5, // maksimal 5 file
                            maxTotalFilesize: 1, // total maksimal 1 MB
                            acceptedFiles: ".pdf,.doc,.docx",
                            dictDefaultMessage:
                              "Klik atau seret file ke sini untuk mengunggah",
                            dictFileTooBig:
                              "File terlalu besar ({{filesize}}MB). Maksimal ukuran file: {{maxFilesize}}MB",
                            dictTotalFilesTooBig:
                              "Total ukuran file melebihi batas maksimal (1 MB)",
                            dictInvalidFileType:
                              "Format file tidak didukung. Hanya PDF dan DOC/DOCX yang diperbolehkan",
                            dictResponseError:
                              "Terjadi kesalahan saat mengunggah file",
                            dictCancelUpload: "Batalkan unggah",
                            dictUploadCanceled: "Unggah dibatalkan",
                            dictCancelUploadConfirmation:
                              "Apakah Anda yakin ingin membatalkan unggah?",
                            dictRemoveFile: "Hapus file",
                            dictMaxFilesExceeded:
                              "Anda tidak dapat mengunggah file lagi",
                          }}
                          onChange={handleDropzoneChange}
                          ref={dropzoneRef}
                        />
                      ),
                      []
                    )}
                  </Grid>
                  <Grid item xs={12} display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      color="info"
                      onClick={handleSubmit}
                      disabled={loading || showSizeWarning || totalSize > 1}
                    >
                      {loading ? "Menyimpan..." : "Simpan Disposisi"}
                    </Button>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DisposisiBaru;
